<template>
  <div>
    <router-view :key="lang"></router-view>
  </div>
</template>

<script>

import Vue from "vue";
import { TranslationService, CookieService, AppService } from "@/services";

/**
 * localStorage is not always there to be used.
 *
 * A password reset link is opened from a mail client, which means an in-app
 * webview or a private window as often as not, and those hand back a store that
 * either throws on write or is emptied between page loads. Neither is a reason to
 * give up on the language, and neither may be allowed to throw out of a promise
 * chain that has work left to do after it.
 */
const readStorage = key => {
  try {
    return localStorage.getItem(key);
  } catch (e) {
    return null;
  }
};

const writeStorage = (key, value) => {
  try {
    localStorage.setItem(key, value);
  } catch (e) {
    // Over quota, or a store the browser refuses to persist. The cookie and the
    // in-memory copy carry the language for this page load either way.
  }
};

export default {
  name: "app",
  data() {
    return {
      translationService: new TranslationService,
      appService: new AppService,
      cookieService: new CookieService,
      lang: null,
      release: null,
    }
  },
  mounted() {
    this.initLanguageWatcher();

    // The language is resolved whether or not the release call answers. It used
    // to hang off that request's success, so a failed or slow /info/release left
    // the app on whatever strings the blade happened to inject, and left the
    // rejection unhandled.
    this.appService
      .findReleaseNumber()
      .then(data => this.applyRelease(data))
      .catch(() => {})
      .then(() => this.resolveLanguage())
      // Translations that will not load leave the app on the strings the blade
      // injected, which is a page in the wrong language rather than no page.
      .catch(() => {});
  },
  methods: {
    /**
     * Drops the cached language cookie when the app has been deployed since this
     * browser last looked, so a release that changes copy is not served stale.
     */
    applyRelease(data) {
      if (!data || readStorage('release') == data.release) return;

      writeStorage('release', data.release);
      this.cookieService.removeCookie('lang');
    },
    /**
     * The user's own preference, else what this browser was last given, else what
     * the browser itself asks for.
     *
     * The last case used to be handled by loading the language and then reloading
     * the page, on the assumption that it happens once because the reload finds
     * `locale` in storage. That holds only where storage survives a reload; where
     * it does not, every load reloaded the page again, and a password reset - the
     * one page people reach in a browser that has never seen the app - could not be
     * completed. The reload was never needed: `lang` keys the router-view, so
     * setting it below re-renders the app with the translations that just arrived.
     */
    resolveLanguage() {
      const iso =
        this.$store.state?.user?.preference?.language?.iso_3166_2 ||
        readStorage('locale') ||
        navigator.language.substr(0, 2);

      return this.fetchLanguage(iso);
    },
    initLanguageWatcher() {
      this.$watch(
        function () {
          return this.$store.state?.user?.preference?.language?.iso_3166_2;
        },
        function (newVal, oldVal) {
          this.cookieService.removeCookie('lang');

          if (newVal) {
            window.axios.defaults.headers.common['X-Locale'] = newVal;

            this.fetchLanguage(newVal).catch(() => {})
          }
        }
      )
    },
    fetchLanguage(isoCode) {
      return this.translationService.findByIso(isoCode).then(data => {
        this.cookieService.setCookie(`lang`, isoCode, 365);
        writeStorage('i18n', JSON.stringify(data));
        writeStorage('locale', isoCode);
        window.i18n = data;
        Vue.prototype.i18n = data;
        this.lang = isoCode;
        this.$forceUpdate();
      })
    }
  }
};
</script>
